import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? getCategoryBySlug(slug) : null;
  const [activeSubSlug, setActiveSubSlug] = useState<string>(slug || '');

  useEffect(() => {
    if (slug) setActiveSubSlug(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSubSlug]);

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-20 pb-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">分类未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const isSub = 'isSubcategory' in category && category.isSubcategory;
  const parentSlug = isSub ? (category as { parentSlug: string }).parentSlug : null;
  const parentName = isSub ? (category as { parentName: string }).parentName : null;

  const subcategories = !isSub && 'subcategories' in category ? category.subcategories : [];
  const allSubSlugs = !isSub ? [category.slug, ...subcategories.map(s => s.slug)] : [slug as string];

  const articles = getArticlesByCategory(activeSubSlug);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    ...(isSub && parentSlug && parentName ? [{ label: parentName, href: `/category/${parentSlug}` }] : []),
    { label: category.name },
  ];

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${category.name} - 亚洲精品一区二区`,
    "description": 'description' in category ? category.description : '',
    "url": `https://tsdtys3.cn/category/${slug}`,
  };

  return (
    <>
      <SchemaOrg schema={pageSchema} />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-20 pb-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <header className="mb-10">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              {category.name}
            </h1>
            {'description' in category && (
              <p className="text-foreground-600 text-sm max-w-2xl leading-relaxed">
                {category.description}
              </p>
            )}
          </header>

          {subcategories.length > 0 && (
            <div className="flex items-center gap-2 mb-8 flex-wrap">
              <button
                onClick={() => setActiveSubSlug(category.slug)}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeSubSlug === category.slug
                    ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                全部
              </button>
              {subcategories.map((sub) => (
                <button
                  key={sub.slug}
                  onClick={() => setActiveSubSlug(sub.slug)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeSubSlug === sub.slug
                      ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {sub.name} ({sub.count})
                </button>
              ))}
            </div>
          )}

          {articles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {articles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-100 rounded-lg overflow-hidden cursor-pointer transition-all duration-300 hover:bg-background-200/50"
                >
                  <div className="h-48 overflow-hidden">
                    <div className="w-full h-full">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      <span className="text-xs bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full whitespace-nowrap">
                        {article.tags[0]}
                      </span>
                      <span className="text-xs text-foreground-500">{article.date}</span>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-foreground-600 text-xs leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="mt-4 flex items-center gap-1 text-primary-600 text-xs whitespace-nowrap">
                      阅读全文 <i className="ri-arrow-right-line text-xs"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-foreground-500 text-sm">该分类下暂无内容</p>
              <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm mt-3 inline-block cursor-pointer">
                返回首页浏览更多内容
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}